<script>
    import { onMount } from "svelte";

    import { allGroups, EmailTypes, isMobile, user } from "../../stores";

    import { fly } from "svelte/transition";

    import {
        SearchInput,
        SearchSelect,
        SvelteTable,
        PageLoading,
        MultiSearchSelect,
        ResetFilterLink,
        TableToggle,
        DefaultDoctorFilter,
        DoctorMultiSearchSelect,
    } from "../../Components/Common/index.svelte";

    import { apiMethods } from "../../Services/index.svelte";

    import { DirectUserRoles } from "../../Utils/utilEnums.svelte";

    import {
        replaceURL,
        getText,
        goToURL,
        showError,
        parseTemplateTypeName,
        decodeSearchParams,
        encodeSearchParams,
        syncDefaultDoctorFilter,
        getBoolean,
    } from "../../Utils/utilFunctions.svelte";

    import { cacheAllGroups } from "../../Utils/utilCache.svelte";

    import {
        checkIcon,
        managerHiddenEmailTemplates,
    } from "../../Utils/utilConstants.svelte";

    import { loadEmailTypes } from "../../Utils/DataLoaders.svelte";

    import TableFooter from "../../Components/Layout/Table/TableFooter.svelte";

    export let params = {};

    $: isUserAdmin = $user.role == DirectUserRoles.Admin;

    $: isUserSupport = $user.role == DirectUserRoles.Support;

    $: isUserManager = $user.role == DirectUserRoles.Manager;

    $: isUserDoctor = $user.role == DirectUserRoles.Doctor;

    $: isUserEnableSearchGroup = isUserAdmin || isUserSupport;

    $: isUserEnableSearchDoctor = isUserAdmin || isUserSupport || isUserManager;

    $: if (isUserAdmin)
        displayColumns = [
            ...columns,
            ...groupColumns.filter((col) => col.key != "groupId"),
            ...accountColumns.filter((col) => col.key != "accountId"),
        ];
    else if (isUserManager)
        displayColumns = [
            ...columns,
            ...personalisedColumns.filter((col) => col.key != "activeToggle"),
            ...accountColumns.filter(
                (col) => col.key != "accountId" && !col.isHidden,
            ),
        ];
    else {
        personalisedColumns[personalisedColumns.length - 1].headerClass +=
            " rounded-tr-xl";
        displayColumns = [...columns, ...personalisedColumns];
    }

    $: if (isUserManager)
        displayTemplateTypes = $EmailTypes.filter(
            (template) => !managerHiddenEmailTemplates.includes(template.id),
        );
    else displayTemplateTypes = $EmailTypes;

    let isGettingEmailTemplates = false,
        returnEmailTemplatesArr = [],
        filters = {},
        page = 0,
        pageSize = 25,
        count = 0,
        showFilters = false;

    let searchGroupsArr = [],
        groupOptions = [];

    let selectedDoctors = [];

    let displayColumns = [];

    let displayTemplateTypes = [];

    const columns = [
        {
            key: "ID",
            title: "ID",
            headerClass: "text-left px-3 w-1/8 cursor-pointer rounded-tl-xl",
            class: "text-left px-3 cursor-pointer",
            renderValue: (v) => getText(v.id),
        },
        {
            key: "name",
            title: "Name",
            headerClass: "text-left px-3 w-auto cursor-pointer",
            class: "text-left px-3 cursor-pointer",
            renderValue: (v) => getText(v.name),
        },
        {
            key: "type",
            title: "Type",
            headerClass: "text-left px-3 w-1/8 cursor-pointer",
            class: "text-left px-3 cursor-pointer",
            renderValue: (v) => parseTemplateTypeName(v.typeName),
        },
    ];

    const groupColumns = [
        {
            key: "groupId",
            title: "Group ID",
            headerClass: "text-left px-3 w-1/8 cursor-pointer",
            class: "text-left px-3 cursor-pointer",
            renderValue: (v) => getText(v.groupId == 0 ? "-" : v.groupId),
        },
        {
            key: "groupName",
            title: "Group Name",
            headerClass: "text-left px-3 w-1/8 cursor-pointer",
            class: "text-left px-3 cursor-pointer",
            renderValue: (v) => getText(v.groupName ?? "-"),
        },
    ];

    const accountColumns = [
        {
            key: "accountId",
            title: "Group ID",
            headerClass: "text-left px-3 w-1/8 cursor-pointer",
            class: "text-left px-3 cursor-pointer",
            renderValue: (v) => getText(v.accountId == 0 ? "-" : v.accountId),
        },
        {
            key: "accountName",
            title: "Account Name",
            headerClass: "text-left px-3 w-1/8 cursor-pointer",
            class: "text-left px-3 cursor-pointer",
            renderValue: (v) => getText(v.accountName ?? "-"),
        },
        {
            key: "activeToggle",
            title: "Active",
            headerClass: "text-left px-3 w-[2%] cursor-pointer rounded-tr-xl",
            class: "text-left px-3 cursor-pointer",
            renderComponent: {
                component: TableToggle,
                props: {
                    valueKey: "active",
                    clickFunction: async (rowData) => {
                        await toggleTemplateActivity(rowData);
                    },
                },
            },
            csvContent: (v) => `${getBoolean(v.active)}`,
        },
    ];

    const personalisedColumns = [
        {
            key: "personalised",
            title: "Personalised",
            headerClass: "text-center px-3 w-1/8 cursor-pointer",
            class: "text-center px-3 cursor-pointer",
            renderValue: (v) => _renderPersonaliseIcon(v),
            csvExportHidden: true,
        },
        {
            key: "activeToggle",
            title: "Active",
            headerClass: "text-left px-3 w-[2%] cursor-pointer rounded-tr-xl",
            class: "text-left px-3 cursor-pointer",
            renderComponent: {
                component: TableToggle,
                props: {
                    valueKey: "active",
                    clickFunction: async (rowData) => {
                        await toggleTemplateActivity(rowData);
                    },
                },
            },
            csvContent: (v) => `${getBoolean(v.active)}`,
        },
    ];

    const searchFilters = [
        {
            key: "type",
            name: "Type",
            filterKey: "filter[type]",
        },
        {
            key: "preDefineDoctor",
            name: "Doctors",
            filterKey: "filter[doctorId]",
            separateHandle: true,
        },
        {
            key: "doctor",
            name: "Doctors",
            filterKey: "filter[doctorId]",
            separateHandle: true,
        },
        {
            key: "group",
            name: "Groups",
            filterKey: "filter[groupId]",
            separateHandle: true,
        },
        {
            key: "search",
            name: "Search",
            filterKey: "search",
        },
        {
            key: "name",
            name: "Name",
            filterKey: "filter[name]",
        },
    ];

    onMount(async () => {
        await loadEmailTypes();

        if (params.query) updateSearchParamsByQuery(params.query);

        filters = await syncDefaultDoctorFilter(filters, "filter[doctorId]");

        await loadEmailTemplates(page * pageSize, pageSize, _getSearchParams());

        if (isUserEnableSearchGroup) {
            await cacheAllGroups();

            groupOptions = $allGroups.map(({ id, name }) => ({
                id,
                type: name,
            }));
        }
    });

    const updateSearchParamsByQuery = (query) => {
        const arr = decodeSearchParams(query).split("&");

        if (arr.length) {
            for (let i = 0; i < arr.length; i++) {
                if (arr[i].includes("page="))
                    page = Number(arr[i].split("=")[1]);

                if (arr[i].includes("size="))
                    pageSize = Number(arr[i].split("=")[1]);

                if (arr[i].includes("search="))
                    filters.search = arr[i].split("=")[1].toString();

                searchFilters.forEach((sf) => {
                    if (arr[i].includes(sf.filterKey)) {
                        filters[sf.key] = arr[i].split("=")[1];
                        if (
                            arr[i].includes("groupId") &&
                            isUserEnableSearchGroup
                        ) {
                            searchGroupsArr = filters[sf.key]
                                .split(",")
                                .map((id) => Number(id));
                            filters[sf.filterKey] = searchGroupsArr.join(",");
                        }

                        if (
                            arr[i].includes("doctorId") &&
                            isUserEnableSearchDoctor
                        ) {
                            if (isNaN(arr[i].split("=")[1])) {
                                filters[sf.filterKey] = arr[i].split("=")[1];
                                selectedDoctors = filters[sf.filterKey]
                                    .split(",")
                                    .map((id) => Number(id));
                            } else {
                                filters[sf.filterKey] = Number(
                                    arr[i].split("=")[1],
                                );
                                selectedDoctors = [
                                    Number(filters[sf.filterKey]),
                                ];
                            }
                        }

                        if (arr[i].includes("type"))
                            filters[sf.key] = Number(filters[sf.key]);
                    }
                });
            }

            updateURL();
        }
    };

    const loadEmailTemplates = async (start, limit, params) => {
        isGettingEmailTemplates = true;

        if (params.sort) params.sort = params.sort.toLowerCase();

        await apiMethods.getMethod
            .getEmailTemplates(start, limit, params)
            .then((res) => _handleEmailTemplates(res))
            .then(() => _updateHeaderSortIcon());

        isGettingEmailTemplates = false;
    };

    const handleSelectDoctor = () => {
        filters.doctorId = selectedDoctors.join(",");

        handleFilterSelect();
    };

    const clearFilterDoctor = () => {
        selectedDoctors = [];

        delete filters.doctorId;

        handleFilterSelect();
    };

    const handleFilterSelect = () => {
        if (searchGroupsArr.length && isUserEnableSearchGroup)
            filters["filter[groupId]"] = searchGroupsArr.join(",");
        else delete filters["filter[groupId]"];

        if (selectedDoctors.length && isUserEnableSearchDoctor)
            filters["filter[doctorId]"] = selectedDoctors.join(",");
        else delete filters["filter[doctorId]"];

        page = 0;
        updateURL();
        loadEmailTemplates(0, pageSize, _getSearchParams());
    };

    const handleClickCell = ({ target: { className } }, rowData, key) => {
        if (rowData.id && key != "activeToggle") {
            goToURL(`/EmailTemplates/view/${rowData.id}`);
        }
    };

    const handleClickCol = ({ target: { className } }, colData, key) => {
        if (filters.sort == key || (!filters.sort && key == "name"))
            filters.sort = `${key}Desc`;
        else filters.sort = key;

        updateURL();
        loadEmailTemplates(0, pageSize, _getSearchParams());
    };

    const onPageChange = (e) => {
        page = Number(e.detail.page);

        updateURL();

        loadEmailTemplates(
            Number(e.detail.firstDataIndex),
            pageSize,
            _getSearchParams(),
        );
    };

    const onPageSizeChange = (newPageSize) => {
        pageSize = newPageSize;
        page = 0;

        updateURL();

        loadEmailTemplates(0, newPageSize, _getSearchParams());
    };

    const updateURL = () => {
        replaceURL(
            `/EmailTemplates/page=${page}&size=${pageSize}${_getParamsString()}`,
        );
    };

    const toggleFilters = () => (showFilters = !showFilters);

    const filterTransition = (node, args) => ($isMobile ? fly(node, args) : "");

    const toggleTemplateActivity = async (data) => {
        data = {
            ...data,
            active: !data.active,
            isUpdating: true,
        };

        await apiMethods.putMethod
            .updateEmailTemplate(data)
            .then((res) => {
                if (!res || res.error)
                    showError(
                        res.error ??
                            "Failed to change template active status, please refresh the page and try again and contact support if the issue persists.",
                    );
                else
                    returnEmailTemplatesArr = returnEmailTemplatesArr.map(
                        (template) => {
                            if (template.id == res.id) return res;
                            return template;
                        },
                    );
            })
            .catch((err) => showError(err));

        data.isUpdating = false;
    };

    // HELPER FUNCTIONS
    const _handleEmailTemplates = async (res) => {
        if (res.error) showError(res.error);
        else {
            if (isUserManager)
                returnEmailTemplatesArr = res.results.filter(
                    (template) =>
                        !managerHiddenEmailTemplates.includes(template.type),
                );
            else returnEmailTemplatesArr = [...res.results];

            returnEmailTemplatesArr = returnEmailTemplatesArr.map(
                (template) => {
                    template.isHidden = isUserAdmin
                        ? false
                        : isUserManager
                          ? template.groupId != $user.groupId
                          : isUserDoctor
                            ? !template.accountId
                            : true;

                    return template;
                },
            );

            count = res.total;
        }
    };

    const _getSearchParams = () => {
        let params = { sort: filters.sort ?? "name" };

        if (filters["filter[groupId]"] && isUserEnableSearchGroup)
            params["filter[groupId]"] = filters["filter[groupId]"];

        if (filters["filter[doctorId]"] && isUserEnableSearchDoctor)
            params["filter[doctorId]"] = filters["filter[doctorId]"];

        searchFilters.forEach((sf) => {
            if (
                !sf.separateHandle &&
                (filters[sf.key] || filters[sf.key] === 0)
            )
                params[sf.filterKey] = filters[sf.key];
        });

        return params;
    };

    const _getParamsString = () => {
        let str = "";
        const params = _getSearchParams();

        if (params) {
            if (params.search) str += `&search=${params.search}`;

            searchFilters.forEach((sf) => {
                if (params[sf.filterKey] || params[sf.filterKey] === 0)
                    str += `&${sf.filterKey}=` + params[sf.filterKey];
            });
        }

        return encodeSearchParams(str);
    };

    const _updateHeaderSortIcon = () => {
        let thRow = document.getElementsByTagName("th");
        const params = _getSearchParams();

        if (params) {
            setTimeout(() => {
                const upSpan = document.createElement("span");
                const downSpan = document.createElement("span");
                upSpan.innerHTML = `<i class="fas fa-sort-alpha-down" />`;
                upSpan.style = "font-size: 10px;";
                downSpan.innerHTML = `<i class="fas fa-sort-alpha-up-alt" />`;
                downSpan.style = "font-size: 10px;";

                Array.from(thRow).forEach((row) => {
                    if (
                        (params.sort == "ID" &&
                            row.childNodes[0].data == "ID") ||
                        (params.sort == "type" &&
                            row.childNodes[0].data == "Type") ||
                        (params.sort == "name" &&
                            row.childNodes[0].data == "Name")
                    )
                        row.appendChild(upSpan);
                    else if (
                        (params.sort == "IDDesc" &&
                            row.childNodes[0].data == "ID") ||
                        (params.sort == "typeDesc" &&
                            row.childNodes[0].data == "Type") ||
                        (params.sort == "nameDesc" &&
                            row.childNodes[0].data == "Name")
                    )
                        row.appendChild(downSpan);
                });
            }, 0);
        }
    };

    const _renderPersonaliseIcon = (template) => {
        if (isUserManager && !template.groupId) return "";

        if (isUserDoctor && !template.accountId) return "";

        return `<div class="flex flex-1 w-full items-center justify-center cursor-pointer">
            <i class="${checkIcon}"></i>
        </div>`;
    };
</script>

<div class="flex flex-col flex-1 gap-y-4">
    {#if $isMobile}
        <button
            class="btn btn-accent btn-sm w-28 flex flex-row gap-x-3"
            on:click={toggleFilters}
        >
            Filters
            <i
                style="font-size:20px"
                class={showFilters ? "fa arrow-up" : "fa arrow-down"}
                >&#xf104;
            </i>
        </button>
    {/if}
    {#if showFilters || !$isMobile}
        <div
            class="flex flex-0 {$isMobile
                ? 'flex-col gap-y-4  items-start'
                : 'justify-between items-end'}"
            in:filterTransition={{ duration: 200, y: -100 }}
        >
            <div class="flex gap-4 flex-wrap items-end">
                {#each searchFilters as searchFilter}
                    {#if searchFilter.key == "type"}
                        <SearchSelect
                            bind:value={filters.type}
                            title={searchFilter.name}
                            onChangeFunc={handleFilterSelect}
                        >
                            <option value="" selected>All Types</option>
                            {#each displayTemplateTypes as templateType}
                                <option
                                    value={templateType.id}
                                    selected={templateType.id == filters.type}
                                >
                                    {templateType.type}
                                </option>
                            {/each}
                        </SearchSelect>
                    {:else if searchFilter.key == "preDefineDoctor"}
                        {#if isUserEnableSearchDoctor}
                            <DefaultDoctorFilter
                                bind:filter={filters}
                                filterPropertyName={"filter[doctorId]"}
                                onChangeFunc={() => {
                                    selectedDoctors =
                                        filters["filter[doctorId]"].split(",");
                                    handleFilterSelect();
                                }}
                            />
                        {/if}
                    {:else if searchFilter.key == "doctor"}
                        {#if isUserEnableSearchDoctor}
                            <DoctorMultiSearchSelect
                                bind:searchResult={selectedDoctors}
                                onChangeFunc={handleSelectDoctor}
                                inputClass="w-32"
                                zIndex="3"
                            />
                        {/if}
                    {:else if searchFilter.key == "group"}
                        {#if isUserEnableSearchGroup}
                            <MultiSearchSelect
                                title={searchFilter.name}
                                bind:searchResult={searchGroupsArr}
                                optionsArr={groupOptions}
                                inputID="emailTemplateGroupSearchInput"
                                onChangeFunc={handleFilterSelect}
                                inputClass="w-32"
                            />
                        {/if}
                    {:else}
                        <SearchInput
                            bind:value={filters[searchFilter.key]}
                            title={searchFilter.name}
                            inputFunc={handleFilterSelect}
                        />
                    {/if}
                {/each}

                <ResetFilterLink
                    bind:filter={filters}
                    onResetFilter={() => {
                        searchGroupsArr = [];
                        selectedDoctors = [];
                        clearFilterDoctor();
                        handleFilterSelect();
                    }}
                />
            </div>
        </div>
    {/if}

    <div class={$isMobile ? "h-auto w-minus48" : "relative flex-1"}>
        {#if isGettingEmailTemplates}
            <PageLoading />
        {:else}
            <div
                class={$isMobile
                    ? "overflow-x-auto overflow-y-visible"
                    : "overflow-y-auto absolute inset-0"}
            >
                <SvelteTable
                    columns={displayColumns}
                    rows={returnEmailTemplatesArr}
                    clickCellFunc={({ detail: { event, row, key } }) => {
                        handleClickCell(event, row, key);
                    }}
                    clickColFunc={({ detail: { event, row, key } }) => {
                        handleClickCol(event, row, key);
                    }}
                    customRow="align-middle"
                />
            </div>
        {/if}
    </div>

    <TableFooter
        {page}
        {pageSize}
        {count}
        tableData={returnEmailTemplatesArr}
        {onPageChange}
        {onPageSizeChange}
        csvConfig={{
            columns: displayColumns,
            data: returnEmailTemplatesArr,
            fileName: "Email Templates",
            allDataApi: async () =>
                await apiMethods.getMethod.getEmailTemplates(
                    0,
                    999999,
                    _getSearchParams(),
                ),
        }}
    />
</div>
